import React from "react";
import { LinearProgress, Paper } from "@mui/material";
import { SkeletonListRow } from "./Skeleton";

/**
 * The dropdown of a server-searched Autocomplete, telling the person what the search is
 * doing - so a slow answer reads as "finding it", never as "nothing there".
 *
 *   searching, no rows yet   three skeleton rows + "Searching..."
 *   searching, rows showing  a thin bar across the top; the earlier rows stay, faded, so
 *                            refining "mar" -> "mark" does not blink the list each pause
 *   empty                    emptyText, in the place the cashier is already looking
 *   failed                   failedText, in red
 *   anything else            the rows, untouched
 *
 * Hand it to MUI as `PaperComponent={SearchDropdownPaper}` with the state in
 * `slotProps={{ paper: { status, hasRows, searchingText, emptyText, failedText } }}`.
 * It must stay a module-level component: a component made inside the screen's render is a
 * new type every render, and MUI would remount the dropdown on every keystroke.
 *
 * It knows nothing about products, so any picker on FindProdFromPos (or any other
 * server search) can take it as it is.
 */
const SearchDropdownPaper = ({
  status,
  hasRows = false,
  searchingText,
  emptyText,
  failedText,
  children,
  ...paperProps
}) => {
  const searching = status === "searching";
  const message = status === "empty" ? emptyText : status === "failed" ? failedText : null;

  return (
    <Paper {...paperProps}>
      {searching && hasRows && (
        <LinearProgress sx={{ height: "2px", mx: "4px", borderRadius: "2px" }} aria-label={searchingText} />
      )}
      {searching && !hasRows && (
        // Mouse-down kept off the input's blur, or a click on the placeholder would close
        // the dropdown the cashier is waiting on.
        <div role="status" aria-live="polite" onMouseDown={(e) => e.preventDefault()} className="py-1">
          {[0, 1, 2].map((i) => (
            <SkeletonListRow key={i} index={i} />
          ))}
          <div className="px-4 pb-1 pt-[2px] text-[12px] text-gray-500">{searchingText}</div>
        </div>
      )}
      {message && (
        <div
          role="status"
          aria-live="polite"
          onMouseDown={(e) => e.preventDefault()}
          className={`px-4 py-3 text-[13px] ${status === "failed" ? "text-red-500 font-medium" : "text-gray-500"}`}
        >
          {message}
        </div>
      )}
      {/* The rows themselves: faded while a newer search is on its way, still clickable -
          each is a real product, and Enter never picks one found for older text. */}
      <div className={searching && hasRows ? "opacity-50 transition-opacity" : undefined}>{children}</div>
    </Paper>
  );
};

export default SearchDropdownPaper;
